import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { ShieldCheck, Check, Loader2, Paperclip, FileText, X, Landmark, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED = ["pdf", "doc", "docx", "jpg", "jpeg", "png", "webp", "txt", "rtf", "odt"];

const BANK = {
  name: "DAAB RECRUITMENT S.L.",
  iban: "BE43 9675 3382 3601",
  bic: "TRWIBEB1XXX",
  address: "Wise, Rue du Trône 100, 3rd floor, Brussels, 1050, Belgium",
};

const PLANS = [
  { key: "Basic (€99)", price: "€99", name: "Basic Verification", desc: "Initial corporate and online verification." },
  { key: "Professional (€249)", price: "€249", name: "Professional Due Diligence", desc: "Detailed company, ownership, recruitment, documentation & risk assessment." },
  { key: "Enhanced (€499+)", price: "€499+", name: "Enhanced Due Diligence", desc: "Extended investigation across multiple entities, jurisdictions and documents." },
];

const SCOPE = [
  "Company registration", "Directors/shareholders", "Registered address", "Active/inactive status",
  "Recruitment licence", "Government licence/authorization", "VAT/tax registration", "Employer legitimacy",
  "Website/domain", "Corporate email", "Bank/payment details", "Litigation/legal records",
  "Insolvency/bankruptcy", "Regulatory sanctions", "Online reputation", "Recruitment fraud indicators",
  "Previous business activity", "Employer/job-order verification", "B2B partner verification",
];

const EMPTY = {
  company_legal_name: "", country: "", registration_number: "", registered_address: "",
  website: "", company_email: "", contact_person: "", your_email: "", your_phone: "",
  reason: "", documents_available: "", concerns: "",
};

export const Verify = () => {
  const [f, setF] = useState(EMPTY);
  const [scope, setScope] = useState([]);
  const [plan, setPlan] = useState("Professional (€249)");
  const [declaration, setDeclaration] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onHash = () => { if (window.location.hash === "#verify") document.getElementById("verify")?.scrollIntoView(); };
    onHash(); window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggleScope = (v) => setScope((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const valid = [];
    for (const file of picked) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`${file.name}: unsupported type`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: exceeds 10MB`); continue; }
      valid.push(file);
    }
    setFiles((p) => [...p, ...valid].slice(0, 5)); e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_legal_name || !f.contact_person || !f.your_email) { toast.error("Please fill company name, contact person and your email."); return; }
    if (!declaration) { toast.error("Please accept the verification declaration."); return; }
    setLoading(true);
    try {
      const attachments = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const up = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachments.push(up.data);
      }
      const res = await axios.post(`${API}/verify-request`, { ...f, verify_scope: scope, plan, declaration, attachments });
      toast.success(res.data.message); setF(EMPTY); setScope([]); setDeclaration(false); setFiles([]);
    } catch (err) { console.error(err); toast.error(err.response?.data?.detail || "Something went wrong."); }
    finally { setLoading(false); }
  };

  const input = "w-full bg-transparent border-b border-[#e4e4e7] py-3 font-body text-base text-[#0a0a0a] placeholder:text-[#a1a1aa] focus:outline-none focus:border-[#ff3b30] transition-colors";

  return (
    <section id="verify" data-testid="verify-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#f5f5f4] border-y border-[#e4e4e7]">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6"><ShieldCheck size={24} className="text-[#ff3b30]" /><span className="font-body text-sm tracking-[0.25em] uppercase text-[#ff3b30]">Verify a recruitment opportunity</span></div>
          <h2 className="font-head uppercase tracking-tighter leading-[0.92] text-4xl md:text-6xl mb-4">Verify before you partner. Verify before you pay.</h2>
          <p className="font-body text-[#52525b] max-w-2xl leading-relaxed mb-12">Independent due diligence on European recruitment agencies, employers and job offers — before you sign an agreement, send candidates or make any payment.</p>
        </Reveal>

        {/* Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16" data-testid="verify-pricing">
          {PLANS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.08}>
              <div
                onClick={() => { setPlan(p.key); document.getElementById("verify-form")?.scrollIntoView({ behavior: "smooth" }); }}
                data-testid={`plan-${i}`}
                className={`cursor-pointer text-left h-full w-full bg-white border p-8 flex flex-col transition-colors ${plan === p.key ? "border-[#ff3b30]" : "border-[#e4e4e7] hover:border-[#0a0a0a]"}`}>
                <div className="font-head text-5xl tracking-tighter text-[#0a0a0a]">{p.price}</div>
                <h3 className="font-head uppercase tracking-tight text-xl mt-3 mb-2">{p.name}</h3>
                <p className="font-body text-sm text-[#52525b] leading-relaxed mb-6">{p.desc}</p>
                <span className={`mt-auto font-head uppercase text-sm tracking-wide ${plan === p.key ? "text-[#ff3b30]" : "text-[#a1a1aa]"}`}>
                  {plan === p.key ? "✓ Selected" : "Select plan"}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="font-body text-xs text-[#a1a1aa] -mt-10 mb-14">Prices shown in EUR. Payment is made by SEPA / SWIFT bank transfer to our EUR account below. Final pricing may depend on country, number of entities, documents and complexity. Verification is a due-diligence assessment and does not guarantee future business performance or eliminate all commercial risk.</p>

        {/* Bank transfer (EUR) */}
        <Reveal>
          <div data-testid="bank-transfer" className="bg-[#0a0a0a] text-white p-8 md:p-10 mb-16">
            <div className="flex items-center gap-3 mb-2">
              <Landmark size={22} className="text-[#ff3b30]" />
              <span className="font-body text-xs tracking-[0.25em] uppercase text-[#ff3b30]">Pay by bank transfer (EUR)</span>
            </div>
            <p className="font-body text-[#a1a1aa] text-sm mb-8 max-w-2xl">
              Payment is made by bank transfer. Send a SEPA (domestic EU) or SWIFT (international) transfer to our
              EUR account below. Please use your <span className="text-white">company name</span> and the
              chosen plan as the payment reference, then upload the transfer confirmation in the form.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
              {[
                ["Account name", BANK.name],
                ["IBAN", BANK.iban],
                ["SWIFT / BIC", BANK.bic],
                ["Bank address", BANK.address],
              ].map(([label, value]) => (
                <div key={label} className="border-b border-[#27272a] pb-4">
                  <div className="font-body text-xs uppercase tracking-wide text-[#52525b] mb-1">{label}</div>
                  <div className="flex items-center gap-3">
                    <span className="font-head tracking-tight text-lg md:text-xl break-all">{value}</span>
                    <button
                      type="button"
                      data-testid={`copy-${label.split(" ")[0].toLowerCase()}`}
                      onClick={() => { navigator.clipboard?.writeText(value); toast.success(`${label} copied`); }}
                      className="text-[#52525b] hover:text-[#ff3b30] shrink-0"
                      aria-label={`Copy ${label}`}
                    >
                      <Copy size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="font-body text-xs text-[#52525b] mt-6">
              SEPA transfers are usually free/low-cost within the EU. For SWIFT transfers from outside SEPA, use the SWIFT/BIC above.
            </p>
          </div>
        </Reveal>

        {/* Intake form */}
        <Reveal>
          <form id="verify-form" onSubmit={submit} data-testid="verify-form" className="bg-white border border-[#e4e4e7] p-8 md:p-12 space-y-10">
            <div>
              <h3 className="font-head uppercase tracking-tight text-2xl mb-6">1 · Company to verify</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input data-testid="v-company" className={input} placeholder="Full legal company name *" value={f.company_legal_name} onChange={s("company_legal_name")} />
                <input data-testid="v-country" className={input} placeholder="Country of registration" value={f.country} onChange={s("country")} />
                <input className={input} placeholder="Company registration number" value={f.registration_number} onChange={s("registration_number")} />
                <input className={input} placeholder="Registered address" value={f.registered_address} onChange={s("registered_address")} />
                <input className={input} placeholder="Website" value={f.website} onChange={s("website")} />
                <input className={input} placeholder="Official company email" value={f.company_email} onChange={s("company_email")} />
                <input data-testid="v-contact" className={input} placeholder="Contact person & position *" value={f.contact_person} onChange={s("contact_person")} />
              </div>
            </div>

            <div>
              <h3 className="font-head uppercase tracking-tight text-2xl mb-4">2 · Reason for verification</h3>
              <textarea rows={3} className={`${input} resize-none`} placeholder="Why verify? (B2B agreement, sending candidates, making a payment, job/work-permit/immigration offers, existing dealings…)" value={f.reason} onChange={s("reason")} />
            </div>

            <div>
              <h3 className="font-head uppercase tracking-tight text-2xl mb-5">3 · What should we verify?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3" data-testid="verify-scope">
                {SCOPE.map((item) => (
                  <button key={item} type="button" onClick={() => toggleScope(item)} data-testid={`scope-${item}`}
                    className="flex items-center gap-2 text-left font-body text-sm">
                    <span className={`w-5 h-5 shrink-0 border flex items-center justify-center transition-colors ${scope.includes(item) ? "bg-[#ff3b30] border-[#ff3b30]" : "border-[#a1a1aa]"}`}>
                      {scope.includes(item) && <Check size={13} className="text-white" strokeWidth={3} />}
                    </span>
                    <span className={scope.includes(item) ? "text-[#0a0a0a]" : "text-[#52525b]"}>{item}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-head uppercase tracking-tight text-2xl mb-4">4 · Documents available</h3>
                <textarea rows={3} className={`${input} resize-none`} placeholder="Registration extract, contracts, invoices, job orders, licences, bank details, correspondence…" value={f.documents_available} onChange={s("documents_available")} />
              </div>
              <div>
                <h3 className="font-head uppercase tracking-tight text-2xl mb-4">5 · Concerns / red flags</h3>
                <textarea rows={3} className={`${input} resize-none`} placeholder="Advance payment requested? Suspicious documents? Changed name/bank details? Reported problems? Suspected fraud?" value={f.concerns} onChange={s("concerns")} />
              </div>
            </div>

            <div>
              <h3 className="font-head uppercase tracking-tight text-2xl mb-5">Your contact details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input data-testid="v-email" type="email" className={input} placeholder="Your email *" value={f.your_email} onChange={s("your_email")} />
                <input className={input} placeholder="Your WhatsApp / Phone" value={f.your_phone} onChange={s("your_phone")} />
              </div>
            </div>

            <div>
              <label htmlFor="v-file" className="flex items-center gap-3 border border-dashed border-[#e4e4e7] hover:border-[#ff3b30] cursor-pointer px-4 py-4 transition-colors">
                <Paperclip size={18} className="text-[#ff3b30]" />
                <span className="font-body text-sm text-[#52525b]">Upload documents (optional)<span className="block text-xs text-[#a1a1aa]">Registration, contracts, invoices, offers · up to 5 files · 10MB each</span></span>
              </label>
              <input id="v-file" data-testid="v-file" type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt,.rtf,.odt" className="hidden" onChange={addFiles} />
              {files.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {files.map((file, i) => (
                    <li key={i} className="flex items-center gap-2 bg-[#f5f5f4] border border-[#e4e4e7] px-3 py-2">
                      <FileText size={15} className="text-[#52525b]" /><span className="font-body text-sm truncate flex-1">{file.name}</span>
                      <button type="button" onClick={() => setFiles((p) => p.filter((_, x) => x !== i))} className="text-[#a1a1aa] hover:text-[#ff3b30]"><X size={15} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <label className="flex items-start gap-3 font-body text-sm text-[#52525b]">
              <input type="checkbox" data-testid="v-declaration" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} className="mt-1 w-4 h-4 accent-[#ff3b30]" />
              <span>I will provide all information and documents available to me. DAAB will conduct an independent verification based on official and reliable sources. Verification is a due-diligence assessment and does not constitute a guarantee of future business performance or eliminate all commercial risks.</span>
            </label>

            <button type="submit" disabled={loading} data-testid="verify-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#ff3b30] hover:bg-[#ff5248] disabled:opacity-60 text-white font-head uppercase tracking-wide px-8 py-4 font-600 transition-colors">
              {loading ? <>Submitting <Loader2 size={18} className="animate-spin" /></> : "Request verification"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
